import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { selectProduct, setVizLoading } from '../../../../store/visualizerSlice';
import ProductList from '../../ProductList/ProductList';
import StepNavigation from '../../StepNavigation/StepNavigation';
import styles from './ExtDoors.module.css';

/**
 * ExtDoors Step Component
 * Uses profile(color) setup - both profile and color must be selected before applying.
 * Applies to surface TYPE "BODY" (not surface name).
 * Sel string format: profile_id(color_id)
 */
export default function ExtDoors({ 
    surfaceType = 'Door',
    stepIndex,
    room = '',
    onViewAllApplied, 
    onNext, 
    onPrevious 
}) {
    const dispatch = useDispatch();
    const stepSelections = useSelector(state => state.visualizer.stepSelections);
    const [activeTab, setActiveTab] = useState('profile'); // 'profile' or 'color'
    const [selectedProfile, setSelectedProfile] = useState(null);
    const [selectedColor, setSelectedColor] = useState(null);

    // Selection keys: StepName_Room_Profile and StepName_Room_Color
    const profileSelectionKey = `ExtDoors_${room}_Profile`;
    const colorSelectionKey = `ExtDoors_${room}_Color`;

    // Restore selections from Redux on mount
    useEffect(() => {
        const savedProfile = stepSelections[profileSelectionKey];
        const savedColor = stepSelections[colorSelectionKey];
        if (savedProfile?.product) {
            setSelectedProfile(savedProfile.product);
        }
        if (savedColor?.product) {
            setSelectedColor(savedColor.product);
        }
    }, [stepSelections, profileSelectionKey, colorSelectionKey]);

    // Apply when both profile and color are selected
    useEffect(() => {
        if (selectedProfile && selectedColor) {
            applyProfileColor(selectedProfile, selectedColor);
        }
    }, [selectedProfile, selectedColor]);

    const handleProfileSelect = (product) => {
        setSelectedProfile(product);
        
        console.log('ExtDoors - Profile selected:', product.color || product.name);
        
        // Store selection in Redux
        dispatch(selectProduct({
            product,
            surfaceName: 'ExtDoors_Profile',
            stepIndex,
            selectionKey: profileSelectionKey
        }));
    };

    const handleColorSelect = (product) => {
        setSelectedColor(product);
        
        console.log('ExtDoors - Color selected:', product.color || product.name);
        
        // Store selection in Redux
        dispatch(selectProduct({
            product,
            surfaceName: 'ExtDoors_Color',
            stepIndex,
            selectionKey: colorSelectionKey
        }));
    };

    const applyProfileColor = (profile, color) => {
        const chamViz = document.getElementById('chamViz');
        if (!chamViz) {
            console.error('ExtDoors - chamViz element not found');
            return;
        }

        if (!profile.id || !color.id) {
            console.error('ExtDoors - Profile or Color has no ID');
            return;
        }

        // Build sel string: profile_id(color_id)
        const selString = `${profile.id}(${color.id})`;
        
        console.log('ExtDoors - Applying profile(color):', selString);
        console.log('ExtDoors - Applying to surface type:', surfaceType);
        
        dispatch(setVizLoading(true));
        
        // Apply by surface TYPE (not name)
        chamViz.applyByFilter(
            selString,
            "",
            function(newSel) {
                // this = surface object { name, type, sortorder, sel }
                if (this.name === surfaceType) {
                    return newSel;
                }
                return this.sel;
            }
        );
    };

    const handleViewAllApplied = () => {
        if (onViewAllApplied) {
            onViewAllApplied();
        }
    };

    return (
        <div className={styles.container}>
            <StepNavigation
                title="Exterior Doors"
                onNext={onNext}
                onPrevious={onPrevious}
                onViewAllApplied={handleViewAllApplied}
            />

            {/* Tab Navigation */}
            <div className={styles.tabContainer}>
                <button
                    className={`${styles.tab} ${activeTab === 'profile' ? styles.tabActive : ''}`}
                    onClick={() => setActiveTab('profile')}
                >
                    PROFILE
                </button>
                <button
                    className={`${styles.tab} ${activeTab === 'color' ? styles.tabActive : ''}`}
                    onClick={() => setActiveTab('color')}
                >
                    COLOR
                </button>
            </div>

            {/* Selection Status */}
            <div className={styles.selectionStatus}>
                <span className={selectedProfile ? styles.selected : ''}>
                    Profile: {selectedProfile ? (selectedProfile.color || selectedProfile.name) : 'Not selected'}
                </span>
                <span className={selectedColor ? styles.selected : ''}>
                    Color: {selectedColor ? (selectedColor.color || selectedColor.name) : 'Not selected'}
                </span>
            </div>

            {/* Product List - Profile Tab */}
            {activeTab === 'profile' && (
                <div className={`${styles.productListContainer} ${styles.extDoorsProducts}`}>
                    <ProductList
                        seriesId="9719"
                        thumbSize="256"
                        onProductSelect={handleProfileSelect}
                        titleAttribute="color"
                        descriptionAttribute="name"
                        gridClassName={styles.productGrid}
                        selectedId={selectedProfile?.id}
                    />
                </div>
            )}

            {/* Product List - Color Tab */}
            {activeTab === 'color' && (
                <div className={`${styles.productListContainer} ${styles.extDoorsProducts}`}>
                    <ProductList
                        seriesId="9726"
                        thumbSize="256"
                        onProductSelect={handleColorSelect}
                        titleAttribute="color"
                        descriptionAttribute="name"
                        gridClassName={styles.productGrid}
                        selectedId={selectedColor?.id}
                    />
                </div>
            )}
        </div>
    );
}

ExtDoors.propTypes = {
    surfaceType: PropTypes.string,
    stepIndex: PropTypes.number.isRequired,
    room: PropTypes.string,
    onViewAllApplied: PropTypes.func,
    onNext: PropTypes.func,
    onPrevious: PropTypes.func
};
